import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { Eye, XClose } from '@kula-ai/icons-core';
import { Text } from '@kula-ai/components';
import { FileTypeTile } from './FileTypeTile';
import { PreviewPdfModal } from '@/components/ats/FileUpload/PreviewPdfModal';
import { canPreview } from '@/components/ats/FileUpload/previewSupport';
import { formatBytes } from '@/utils/format-bytes';
import type { SelectedAttachment } from './useOfferAttachments';

export interface AttachmentChipProps {
  attachment: SelectedAttachment;
  // Deselect ONLY — never a delete/purge call (contract seam_2 MUST_NOT, case X1).
  onRemove: (_attachment: SelectedAttachment) => void;
  /**
   * The file is not on the offer yet — being uploaded, or its selection still
   * being saved. The chip keeps its shape and shows a loader where the actions
   * are: preview and ✕ would both act on something the server does not have,
   * and hiding the chip entirely until it lands makes Add look like it did
   * nothing.
   */
  loading?: boolean;
}

const ICON_TILE_SIZE = 32;

/**
 * One attached document, as a compact chip: file tile, then the name stacked
 * over its size, then preview and remove.
 *
 * Not `ViewFile` (the offer template's file rows) because that lays name and
 * size out as flex siblings on one line — at chip width they squeeze each
 * other, which is how a size renders as "5…". The design stacks them, so the
 * name truncates on its own and the size always reads in full.
 *
 * Not the kula `AttachmentPreview` chip either, which is the right shape but
 * offers removal only. Previewing an attachment before sending it for approval
 * is the point of the chip here — `AttachmentPreview` can't do it, which looks
 * like it belongs upstream in @kula-ai/components (an `onPreview` alongside its
 * `onRemove`).
 */
export const AttachmentChip = ({
  attachment,
  onRemove,
  loading = false,
}: AttachmentChipProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const file = {
    filename: attachment.filename,
    content_type: attachment.content_type,
    // AMENDMENT 1 (contract.yml) — always present, a plain signed blob URL for
    // every ref_type (amendment_6 made scorecards a normal pre-generated blob
    // too). The client never needed to special-case it either way.
    url: attachment.url,
  };

  return (
    <>
      <Flex
        align="center"
        // Figma 2673:4691 — 8px between the tile and the text column.
        gap="8px"
        p="6px"
        w="100%"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="6px"
        bg="base-white"
        data-test-id="attachment-chip"
        data-loading={loading ? 'true' : undefined}
      >
        {/* The loader takes the tile's place, not the actions': mid-upload the
            file's kind is not settled yet, while its name and size are already
            known and worth showing. */}
        <FileTypeTile
          filename={attachment.filename}
          mimeType={attachment.content_type}
          size={ICON_TILE_SIZE}
          loading={loading}
        />

        {/* minW=0 or the truncation below never engages: a flex item defaults
            to min-width:auto and refuses to shrink past its content. */}
        <Flex direction="column" minW={0} flex={1}>
          {/* Figma: 12px/650. `small` is 12px font-demi (550), so the weight
              is nudged rather than the size. */}
          <Text
            variant="small"
            color="heading"
            className="!font-display"
            truncate
            tooltip
          >
            {attachment.filename}
          </Text>
          {/* Figma: secondary-400. kula Text names that colour `disabled`,
              which would be wrong here — a size is not a disabled state — so
              the token is applied directly. */}
          <Text variant="caption" className="!text-secondary-400">
            {/* byte_size is required on every SelectedAttachment (contract.yml
                seam_1 + amendment_6) — always a real integer. */}
            {formatBytes(attachment.byte_size)}
          </Text>
        </Flex>

        <Flex align="center" gap="4px" flexShrink={0}>
          {canPreview(file) && (
            <Box
              as="button"
              type="button"
              aria-label={`Preview ${attachment.filename}`}
              onClick={onOpen}
              lineHeight={0}
              color="secondary-400"
              _hover={{ color: 'secondary-600' }}
            >
              <Eye size={18} />
            </Box>
          )}
          <Box
            as="button"
            type="button"
            // "Remove", not "Delete": the ✕ deselects the reference and never
            // purges the underlying blob (contract.yml seam_2 MUST_NOT).
            aria-label={`Remove ${attachment.filename}`}
            onClick={() => onRemove(attachment)}
            lineHeight={0}
            color="secondary-400"
            _hover={{ color: 'secondary-600' }}
          >
            <XClose size={18} />
          </Box>
        </Flex>
      </Flex>
      {isOpen && !loading && (
        <PreviewPdfModal isOpen={isOpen} onClose={onClose} file={file} />
      )}
    </>
  );
};
